import { useEffect, useState } from 'react';

interface RefreshCountdown {
  canRefresh: boolean;
  minutesUntilRefresh: number;
}

// Owns its own ticker so the parent doesn't re-render every minute. The
// 60-second pulse is enough to keep the per-minute label fresh, but on its
// own it leaves the button disabled for up to a full minute past expiry
// (the tick that flips canRefresh fires up to 59s late). We schedule a
// one-shot setTimeout at the exact cooldown end so the button enables
// promptly. The hook also re-syncs immediately when the tab becomes
// visible or is restored from bfcache, for the sleep-then-return case.
export const useRefreshCountdown = (
  lastRefreshTime: number | null,
  cooldownMs: number,
): RefreshCountdown => {
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);

    // One-shot timer that fires exactly when the current cooldown elapses,
    // so canRefresh transitions to true without waiting up to 60s for the
    // next interval tick. Re-scheduled whenever lastRefreshTime changes.
    let expiryTimer: ReturnType<typeof setTimeout> | null = null;
    if (lastRefreshTime !== null) {
      const remaining = cooldownMs - (Date.now() - lastRefreshTime);
      if (remaining > 0) {
        // +50ms slop so the comparison below comfortably crosses the
        // threshold even if the timer fires a hair early.
        expiryTimer = setTimeout(() => setNow(Date.now()), remaining + 50);
      }
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setNow(Date.now());
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setNow(Date.now());
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      if (expiryTimer) clearTimeout(expiryTimer);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastRefreshTime, cooldownMs]);

  const canRefresh =
    lastRefreshTime === null || now - lastRefreshTime >= cooldownMs;

  // Cap at the cooldown's full minute count so a clock skew can't make the
  // label say something larger than the cooldown itself.
  const cooldownMinutes = Math.ceil(cooldownMs / 60000);
  const minutesUntilRefresh =
    canRefresh || lastRefreshTime === null
      ? 0
      : Math.min(
          Math.ceil((cooldownMs - (now - lastRefreshTime)) / 60000),
          cooldownMinutes,
        );

  return { canRefresh, minutesUntilRefresh };
};
